/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    VStack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {AlertIcon, BrandLogo} from '@salesforce/retail-react-app/app/components/icons'
import {ErrorIcon} from '../icons'
import StandardLogin from '../../components/standard-login'
import PasswordlessLogin from '../../components/passwordless-login'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'
import {CollectifLogoGarage, CollectifLogoDynamite} from '../icons'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useBrand} from '../../contexts/brand-context'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const LoginForm = ({
    submitForm,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    clickCreateAccount = noop,
    form,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType,
    prefix = '',
    onClose
}) => {
    const {isGarage} = useBrand()
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const enabledIDPs = getSiteEnvVarValue(config.app, 'SFID_SOCIAL_LOGIN_ENABLED_IDPS', siteId)
    const hasSocialOptions =
        enabledIDPs && enabledIDPs.trim() && enabledIDPs.toLowerCase() !== 'false'
    const shouldShowSocial = isSocialEnabled && hasSocialOptions && idps.length > 0
    const location = useLocation()
    const navigate = useNavigation()
    const isLoginPage = location.pathname === '/login'

    const handleCreateAccountClick = () => {
        if (isLoginPage) {
            navigate('/registration')
            return
        }
        clickCreateAccount()
    }

    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={4} my={8}>
                {isGarage ? (
                    <>
                        <CollectifLogoGarage width="195px" height="32.29px" />
                    </>
                ) : (
                    <>
                        <CollectifLogoDynamite width="240px" height="31.28px" />
                    </>
                )}
            </Stack>
            <form
                id="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
                noValidate
            >
                {form.formState.errors?.global && (
                    <Alert
                        status="error"
                        marginBottom={4}
                        p={0}
                        backgroundColor="transparent"
                        border="none"
                        alignItems="flex-start"
                    >
                        <ErrorIcon
                            w="13px"
                            h="13px"
                            color="webTypo.textError"
                            flexShrink={0}
                            m="auto 0px"
                            ml={{base: 4, lg: 0}}
                        />
                        <Text fontSize="sm" ml={2} color="webTypo.textError">
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <Stack spacing={6} px={0} px={{base: 4, sm: 0}}>
                    {isPasswordlessEnabled ? (
                        <PasswordlessLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                            idps={idps}
                            setLoginType={setLoginType}
                            enabledIDPs={enabledIDPs}
                            shouldShowSocial={shouldShowSocial}
                        />
                    ) : (
                        <StandardLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            idps={idps}
                            enabledIDPs={enabledIDPs}
                            shouldShowSocial={shouldShowSocial}
                            isLoginPage={isLoginPage}
                            prefix={prefix}
                        />
                    )}

                    <Stack px={isLoginPage ? {base: 4, lg: 0} : 0}>
                        <Stack direction="row" spacing={1}>
                            <Text fontSize={{base: 'xs', lg: 'sm'}}>
                                <FormattedMessage
                                    defaultMessage="Don't have an account?"
                                    id="login_form.message.dont_have_account"
                                />
                            </Text>
                            <Button
                                data-testid="login-form-link-create-account"
                                variant="link"
                                size="xs"
                                fontSize={{base: 'xs', lg: 'sm'}}
                                onClick={handleCreateAccountClick}
                            >
                                <FormattedMessage
                                    defaultMessage="Register"
                                    id="auth_register.tab"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    handleForgotPasswordClick: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func,
    prefix: PropTypes.string,
    onClose: PropTypes.func
}

export default LoginForm
